import { useState, useContext, useEffect, useMemo } from 'react'
import WizardDataContext from '../../../store/WizardDataContext';

import { FormGroup, Checkbox, FormControlLabel, Input } from "@mui/material";
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import NewTermImg from '../../../assets/img/ad/new-term.svg'

// Memoized constant array - moved outside component to prevent recreation on every render
const DEFAULT_INSTRUCTION_ARR = [
  { id: 1, instruction: 'Simply navigate to their website or online store', checked: false },
  { id: 2, instruction: 'You must register online for this discount', checked: false },
  { id: 3, instruction: "Install the vendor's app", checked: false },
  { id: 4, instruction: "Use ID or Driver's License for Age Verification", checked: false },
  { id: 5, instruction: 'Place your first order', checked: false },
  { id: 6, instruction: 'Present to salesperson after receiving a quote', checked: false },
  { id: 7, instruction: 'Show this code to the teller in-store', checked: false },
  { id: 8, instruction: 'Enter the code on the online checkout page before payment', checked: false },
  { id: 9, instruction: 'Call for appointment', checked: false },
  { id: 10, instruction: 'Email for appointment', checked: false },
  { id: 11, instruction: 'WhatsApp for appointment', checked: false },
  { id: 12, instruction: 'Use Student ID card to verify enrolment status', checked: false },
];

export default function Instructions({ setDisableBtn }) {
  const wizardCtx = useContext(WizardDataContext);
  const { wizardData, updateData } = wizardCtx;
  const terms = wizardData.terms;

  const [allInstructions, setAllInstructions] = useState(DEFAULT_INSTRUCTION_ARR);
  const [newInstruction, setNewInstruction] = useState("");
  const [instructionOpen, setInstructionOpen] = useState(false);
  const [instructionMaxLimit, setInstructionMaxLimit] = useState(false);

  const style = { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 600, bgcolor: 'background.paper', border: '2px solid #000', boxShadow: 24, p: 4, borderRadius: '5px'};

  useEffect(() => {
    // Check if at least one instruction is selected
    const hasSelectedInstructions = terms.instructions && terms.instructions.length > 0;
    setDisableBtn(!hasSelectedInstructions);
  }, [terms.instructions, setDisableBtn])

  // Restore instructions selection from context when component mounts
  useEffect(() => {
    // Start with the initial instructions - use memoized constant
    let updatedInstructions = [...DEFAULT_INSTRUCTION_ARR];
    
    // Add custom instructions from context if they exist
    if (terms.customInstructions) {
      updatedInstructions = [...updatedInstructions, ...terms.customInstructions];
    }
    
    // Update checked state based on context
    updatedInstructions.forEach(instruction => {
      const isSelected = terms.instructions && terms.instructions.includes(instruction.instruction);
      instruction.checked = isSelected;
    });
    
    setAllInstructions(updatedInstructions);
  }, [terms.instructions, terms.customInstructions]);

  const handleInputChange = (value, setValue, setLimit) => {
    setValue(value);
    setLimit(value.length > 100);
  };

  const handleAddItem = (newItem, setNewItem, allItems, setAllItems, setOpen) => {
    if (newItem && newItem.length <= 100) {
      const newId = Math.max(...allItems.map(item => item.id)) + 1;
      const newItemObj = { id: newId, instruction: newItem, checked: true };
      allItems.push(newItemObj);
      
      // Store custom instruction in context
      const currentCustomInstructions = terms.customInstructions || [];
      const updatedCustomInstructions = [...currentCustomInstructions, newItemObj];
      
      const currentItems = terms.instructions || [];
      updateData('terms', { 
        instructions: [...currentItems, newItem],
        customInstructions: updatedCustomInstructions
      });
    }
    setNewItem("");
    setOpen(false);
  };

  const handleDeleteItem = (index, allItems, setAllItems) => {
    const itemToDelete = allItems[index];
    
    // Only allow deletion of custom instructions (id > 12), not default ones
    if (itemToDelete && itemToDelete.id > 12) {
      setAllItems((prev) => prev.filter((_, i) => i !== index));
      
      // Remove from context as well
      removeItem(itemToDelete.instruction);
      
      // Remove from custom instructions in context
      const currentCustomInstructions = terms.customInstructions || [];
      const updatedCustomInstructions = currentCustomInstructions.filter(item => item.id !== itemToDelete.id);
      
      updateData('terms', { 
        customInstructions: updatedCustomInstructions
      });
    }
  };

  const handleSelectionChange = (e, item, allItems, setAllItems) => {
    if (e.target.checked) {
      const currentItems = terms.instructions || [];
      updateData('terms', { instructions: [...currentItems, item.instruction] });
      setAllItems(prev => prev.map(instruction => 
        instruction.id === item.id ? { ...instruction, checked: true } : instruction
      ));
    } else {
      // Only remove from context, don't remove from local state
      removeItem(item.instruction);
      setAllItems(prev => prev.map(instruction => 
        instruction.id === item.id ? { ...instruction, checked: false } : instruction
      ));
    }
  };

  const removeItem = (label) => {
    const currentItems = terms.instructions || [];
    const index = currentItems.indexOf(label);
    if (index !== -1) {
      const tempArray = currentItems.slice();
      tempArray.splice(index, 1);
      updateData('terms', { instructions: tempArray });
    }
  };

  const isItemSelected = (item) => {
    const items = terms.instructions || [];
    return items.indexOf(item.instruction) !== -1;
  };

  return (
    <>
      <div className="step6-container">
        <div className="step6">
          <div className="heading">
            <h1>Instructions</h1>
            <h3>Select or add instructions pertaining to your promotional offer</h3>
          </div>
          <FormGroup className="select-terms">
            {allInstructions && allInstructions.length > 0 ? (
              allInstructions.map((item, i) => (
                <div key={item.id} className="term">
                  <FormControlLabel 
                    onClick={(e) => handleSelectionChange(e, item, allInstructions, setAllInstructions)} 
                    control={<Checkbox checked={isItemSelected(item)} />} 
                    label={item.instruction} 
                  /> 
                  {/* Only show X button for custom instructions (id > 12) */}
                  {item.id > 12 && (
                    <CloseRoundedIcon 
                      className="close" 
                      onClick={() => handleDeleteItem(i, allInstructions, setAllInstructions)} 
                    />
                  )}
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666', fontStyle: 'italic',backgroundColor: '#f5f5f5',borderRadius: '8px',margin: '10px 0'}}>
                No instructions available. Click "Create New One" to add some.
              </div>
            )}
          </FormGroup>
          <div className="create-new" onClick={() => setInstructionOpen(true)}>
            <AddRoundedIcon /> <span>CREATE NEW ONE</span>
          </div>
        </div>

        <Modal
          open={instructionOpen}
          onClose={() => setInstructionOpen(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <div className="new-term-container">
              <div className="new-term">
                <div className="heading">
                  <h1>Add a new instruction</h1>
                  <h3>Enter just 1 instruction at a time</h3>
                </div>
                <div className="image">
                  <img src={NewTermImg} width={200} height={200} alt="new instruction" />
                </div>
                <div className="input">
                  <Input 
                    value={newInstruction} 
                    placeholder="Ex: Present this coupon at the counter to redeem" 
                    onChange={(e) => handleInputChange(e.target.value, setNewInstruction, setInstructionMaxLimit)} 
                  />
                  <span className={instructionMaxLimit ? "helper-text warning" : "helper-text"}>MAX 100 CHARACTERS</span>
                </div>
                <div className="blue-btn">
                  <Button onClick={() => handleAddItem(newInstruction, setNewInstruction, allInstructions, setAllInstructions, setInstructionOpen)} disabled={instructionMaxLimit} style={{ marginTop: '10px' }}>
                    <AddRoundedIcon /> <span>ADD</span>
                  </Button>
                </div>
                <div className="btn-close">
                  <CloseRoundedIcon onClick={() => setInstructionOpen(false)} />
                </div>
              </div>
            </div>
          </Box>
        </Modal>
      </div>
    </>
  )
}
